import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { SlidersHorizontal } from "@phosphor-icons/react/SlidersHorizontal";
import { X } from "@phosphor-icons/react/X";

export interface NumericRangeInputProps {
	/** Label for the range input */
	label: string;
	/** Current range value [min, max] */
	value: [number, number];
	/** Callback when range changes */
	onChange: (value: [number, number]) => void;
	/** Default minimum value - used for initial value and reset */
	defaultMin: number;
	/** Default maximum value - used for initial value and reset */
	defaultMax: number;
	/** Minimum allowed value (for validation) */
	min?: number;
	/** Maximum allowed value (for validation) */
	max?: number;
	/** Show slider by default */
	showSlider?: boolean;
	/** Allow user to toggle slider visibility */
	allowSliderToggle?: boolean;
	/** Size variant */
	size?: "small" | "medium";
	/** Error message */
	error?: string;
	/** Helper text */
	helperText?: string;
	/** Disabled state */
	disabled?: boolean;
	/** Step value for slider */
	step?: number;
}

export function NumericRangeInput({
	label,
	value,
	onChange,
	defaultMin,
	defaultMax,
	min,
	max,
	showSlider = false,
	allowSliderToggle = true,
	size = "medium",
	error,
	helperText,
	disabled = false,
	step = 1,
}: NumericRangeInputProps): React.JSX.Element {
	const [minValue, maxValue] = value;

	// Use defaults for validation constraints if not provided
	const effectiveMin = min ?? defaultMin;
	const effectiveMax = max ?? defaultMax;
	const [sliderVisible, setSliderVisible] = React.useState(showSlider);
	const [localMinValue, setLocalMinValue] = React.useState(minValue.toString());
	const [localMaxValue, setLocalMaxValue] = React.useState(maxValue.toString());

	// Sync local state with props
	React.useEffect(() => {
		setLocalMinValue(minValue.toString());
		setLocalMaxValue(maxValue.toString());
	}, [minValue, maxValue]);

	// Validation
	const isMinValid =
		!Number.isNaN(Number(localMinValue)) &&
		Number(localMinValue) >= effectiveMin &&
		Number(localMinValue) <= effectiveMax;
	const isMaxValid =
		!Number.isNaN(Number(localMaxValue)) &&
		Number(localMaxValue) >= effectiveMin &&
		Number(localMaxValue) <= effectiveMax;
	const isRangeValid =
		isMinValid && isMaxValid && Number(localMinValue) <= Number(localMaxValue);
	const hasError = !!error || !isRangeValid;

	const handleMinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.value;
		setLocalMinValue(newValue);

		const numValue = Number(newValue);
		if (
			!Number.isNaN(numValue) &&
			numValue >= effectiveMin &&
			numValue <= effectiveMax &&
			numValue <= maxValue
		) {
			onChange([numValue, maxValue]);
		}
	};

	const handleMaxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.value;
		setLocalMaxValue(newValue);

		const numValue = Number(newValue);
		if (
			!Number.isNaN(numValue) &&
			numValue >= effectiveMin &&
			numValue <= effectiveMax &&
			numValue >= minValue
		) {
			onChange([minValue, numValue]);
		}
	};

	const handleSliderChange = (_: Event, newValue: number | number[]) => {
		const [newMin, newMax] = newValue as number[];
		if (typeof newMin === "number" && typeof newMax === "number") {
			onChange([newMin, newMax]);
		}
	};

	const handleMinBlur = () => {
		const numValue = Number(localMinValue);
		if (
			Number.isNaN(numValue) ||
			numValue < effectiveMin ||
			numValue > effectiveMax ||
			numValue > maxValue
		) {
			setLocalMinValue(minValue.toString());
		}
	};

	const handleMaxBlur = () => {
		const numValue = Number(localMaxValue);
		if (
			Number.isNaN(numValue) ||
			numValue < effectiveMin ||
			numValue > effectiveMax ||
			numValue < minValue
		) {
			setLocalMaxValue(maxValue.toString());
		}
	};

	const toggleSlider = () => {
		setSliderVisible(!sliderVisible);
	};

	const handleClear = () => {
		onChange([defaultMin, defaultMax]);
	};

	const isNonDefault = minValue !== defaultMin || maxValue !== defaultMax;

	const getErrorMessage = () => {
		if (error) return error;
		if (!isMinValid)
			return `Minimum value must be between ${effectiveMin} and ${effectiveMax}`;
		if (!isMaxValid)
			return `Maximum value must be between ${effectiveMin} and ${effectiveMax}`;
		if (!isRangeValid)
			return "Minimum value must be less than or equal to maximum value";
		return "";
	};

	return (
		<FormControl
			error={hasError}
			disabled={disabled}
			size={size}>
			<Stack spacing={1}>
				{/* Label, Clear, and Toggle */}
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between">
					<FormLabel component="legend">{label}</FormLabel>
					<Stack
						direction="row"
						alignItems="center"
						spacing={1}>
						{isNonDefault && (
							<Button
								variant="text"
								size="small"
								onClick={handleClear}
								disabled={disabled}
								startIcon={<X size={14} />}
								sx={{ fontSize: "0.75rem", py: 0.5, color: "text.secondary" }}
								aria-label={`Clear ${label} filter`}>
								Clear
							</Button>
						)}
						{allowSliderToggle && (
							<IconButton
								size="small"
								onClick={toggleSlider}
								disabled={disabled}
								aria-label={sliderVisible ? "Hide slider" : "Show slider"}>
								<SlidersHorizontal
									size={16}
									weight={sliderVisible ? "fill" : "regular"}
								/>
							</IconButton>
						)}
					</Stack>
				</Stack>

				{/* Input Fields */}
				<Stack
					direction="row"
					alignItems="center"
					spacing={1}>
					<TextField
						value={localMinValue}
						onChange={handleMinChange}
						onBlur={handleMinBlur}
						type="number"
						size={size}
						placeholder="Min"
						slotProps={{
							htmlInput: {
								min: effectiveMin,
								max: effectiveMax,
								step,
								"aria-label": `${label} minimum value`,
							},
						}}
						error={hasError && !isMinValid}
						disabled={disabled}
						sx={{ flex: 1 }}
					/>

					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ px: 1 }}>
						to
					</Typography>

					<TextField
						value={localMaxValue}
						onChange={handleMaxChange}
						onBlur={handleMaxBlur}
						type="number"
						size={size}
						placeholder="Max"
						slotProps={{
							htmlInput: {
								min: effectiveMin,
								max: effectiveMax,
								step,
								"aria-label": `${label} maximum value`,
							},
						}}
						error={hasError && !isMaxValid}
						disabled={disabled}
						sx={{ flex: 1 }}
					/>
				</Stack>

				{/* Optional Slider */}
				{sliderVisible && (
					<Box sx={{ px: 1, py: 2 }}>
						<Slider
							value={[minValue, maxValue]}
							onChange={handleSliderChange}
							min={effectiveMin}
							max={effectiveMax}
							step={step}
							disabled={disabled}
							valueLabelDisplay="auto"
							aria-label={`${label} range slider`}
							sx={{
								"& .MuiSlider-thumb": {
									"&:focus, &:hover, &.Mui-active": {
										boxShadow: "var(--mui-shadows-8)",
									},
								},
							}}
						/>
					</Box>
				)}

				{/* Helper Text / Error */}
				{(hasError || helperText) && (
					<FormHelperText>
						{hasError ? getErrorMessage() : helperText}
					</FormHelperText>
				)}
			</Stack>
		</FormControl>
	);
}
